import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IonIcon } from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import { catchError, of, switchMap, timer } from 'rxjs';
import { alarmOutline, alertCircleOutline } from 'ionicons/icons';

import { AuthUserService } from '../../../../services/auth-user.service';
import { CollectionPendingTasksService } from '../../../../services/infrastructure/collection-pending-tasks/collection-pending-tasks.service';
import { CollectionPendingTask } from '../../../../interfaces/collection-pending-task.interface';
import {
  PENDING_TASK_CHIP_THRESHOLD_MS,
  getPendingTaskDueDistanceLabel,
  getPendingTaskTimeState,
  needsImmediateAttention,
} from '../../../../utils';

/**
 * Cada cuánto se vuelve a preguntar por los pendientes del usuario. No hay
 * push ni un endpoint dedicado: el polling es la solución temporal acordada.
 * Con un umbral de 1 hora, 2 minutos deja un margen holgado — en el peor caso
 * el chip aparece con 58 minutos de sobra. Si más adelante la plataforma tiene
 * websockets, esto migra ahí.
 */
const POLL_INTERVAL_MS = 2 * 60 * 1000;

/**
 * Cuántos pendientes se traen por vuelta. El backend ordena por `dueAt`
 * ascendente, así que los vencidos y los más próximos siempre caen en la
 * primera página: no hace falta paginar para saber si hay algo urgente.
 */
const POLL_PAGE_SIZE = 20;

/**
 * Chip de vencimiento próximo, pensado para vivir en el marco de la
 * aplicación y verse en cualquier página mientras el usuario tenga al menos un
 * pendiente vencido o a menos de **1 hora** de vencer.
 *
 * Ese umbral es más estricto que el del badge "Vence pronto" de las tarjetas
 * (3 h), a propósito: son dos niveles de escalamiento distintos — el badge
 * avisa, el chip interrumpe. Coincide con el recordatorio de <1h del backend,
 * no con el correo de <3h.
 *
 * **No se puede descartar.** Es la corrección directa al defecto del legado:
 * ahí "Ver Pendientes" solo pintaba badges de color que no forzaban ninguna
 * acción, y el resultado fueron pendientes vencidos desde 2019. El chip
 * desaparece cuando el pendiente se resuelve, no cuando el usuario lo cierra
 * — por eso no tiene botón de cerrar.
 */
@Component({
  selector: 'app-pending-task-due-soon-chip',
  templateUrl: './pending-task-due-soon-chip.component.html',
  styleUrls: ['./pending-task-due-soon-chip.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IonIcon],
})
export class PendingTaskDueSoonChipComponent {
  private readonly pendingTasksService = inject(CollectionPendingTasksService);
  private readonly authUser = inject(AuthUserService);
  private readonly destroyRef = inject(DestroyRef);

  /** Intervalo de polling, expuesto por si el host quiere afinarlo */
  pollIntervalMs = input<number>(POLL_INTERVAL_MS);

  /** Ventana que enciende el chip; 1 hora por default */
  thresholdMs = input<number>(PENDING_TASK_CHIP_THRESHOLD_MS);

  /**
   * El usuario hizo clic: el host debe llevar a la vista personal de
   * Pendientes filtrada por "vence pronto".
   */
  chipClicked = output<void>();

  /** Pendientes abiertos vencidos o dentro de la ventana de `thresholdMs` */
  urgentTasks = signal<CollectionPendingTask[]>([]);

  /** Reloj propio: el estado de tiempo cambia aunque la lista no se haya movido */
  now = signal(new Date());

  readonly hasUrgentTasks = computed(() => this.urgentTasks().length > 0);

  /** true si al menos uno ya venció — el chip sube de tono */
  readonly hasOverdue = computed(() =>
    this.urgentTasks().some((task) => getPendingTaskTimeState(task, this.now()) === 'overdue'),
  );

  /**
   * Texto del chip. Con un solo pendiente dice cuánto falta (o cuánto lleva
   * vencido); con varios, el conteo — el detalle está a un clic.
   */
  readonly label = computed(() => {
    const tasks = this.urgentTasks();
    if (tasks.length === 0) return '';
    if (tasks.length === 1) {
      return getPendingTaskDueDistanceLabel(tasks[0].dueAt, this.now());
    }
    return `${tasks.length} pendientes por atender`;
  });

  constructor() {
    addIcons({ alarmOutline, alertCircleOutline });

    timer(0, this.pollIntervalMs())
      .pipe(
        switchMap(() => {
          this.now.set(new Date());
          const userId = this.authUser.userId();
          if (userId === null) return of(null);

          return this.pendingTasksService
            .getPendingTasks({
              assignedUserId: userId,
              status: 'open',
              page: 1,
              limit: POLL_PAGE_SIZE,
            })
            .pipe(
              catchError((err) => {
                // Un fallo de red no debe tumbar el polling: se reintenta en la
                // siguiente vuelta con la lista anterior intacta.
                console.error('❌ Error al consultar pendientes por vencer:', err);
                return of(null);
              }),
            );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        if (!response) return;
        const reference = this.now();
        const withinMs = this.thresholdMs();
        this.urgentTasks.set(
          response.pendingTasks.filter((task) =>
            needsImmediateAttention(task, withinMs, reference),
          ),
        );
      });
  }
}
